// Mini modal with picture separated by a vertical line
import React from 'react';
import { css } from '@emotion/react';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import { type RestFortySixtyMiniModal1Modal } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import {
  Avatar,
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

import './modal.scss';

type Props = {
  modalInfo: RestFortySixtyMiniModal1Modal;
  close: () => void;
  detailedAction: React.ReactElement;
};

const RenderFortySixtyMiniModal1V2: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      info: { title, details },
      person,
    },
  },
  close,
  detailedAction,
}) => {
  const { mobileView } = useMediaQueries();

  if (mobileView) {
    return (
      <ModalMobileInnerSpacer
        className={cs(
          'forty-sixty-mini-1',
          'display-grid',
          'mobile',
          'inner-spacer'
        )}
      >
        <OfferTitleMobile
          title={<TitleTheme data-part="title" title={title} />}
          close={close}
        />
        <div
          css={css`
            border-bottom: 1px solid #e5e5e5;
            padding-bottom: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <Avatar data-part="person" person={person} />
        </div>
        <div data-part="description">
          <CustomerHtml value={details} />
        </div>
        <div>{detailedAction}</div>
      </ModalMobileInnerSpacer>
    );
  }

  return (
    <ModalDesktopInnerSpacer
      css={css`
        max-width: 600px;
      `}
      className={cs(
        'forty-sixty-mini-1',
        'desktop',
        'display-grid',
        'inner-spacer'
      )}
    >
      <OfferTitleMobile
        title={<TitleTheme data-part="title" title={title} />}
        close={close}
      />
      <div
        className={cs('display-grid', 'content-grid', 'align-items--center')}
      >
        <div
          css={css`
            border-right: 1px solid #e5e5e5;
            padding-right: 1rem;
          `}
        >
          <div>
            <Avatar data-part="person" person={person} vertical />
          </div>
        </div>
        <div
          css={css`
            padding-left: 2rem;
          `}
        >
          <div
            css={css`
              margin-bottom: 1.5rem;
              overflow-y: auto;
              max-height: 170px;
            `}
            data-part="description"
          >
            <CustomerHtml value={details} />
          </div>
          <div>{detailedAction}</div>
        </div>
      </div>
    </ModalDesktopInnerSpacer>
  );
};

const FortySixtyMiniModal1V2 = Object.assign(RenderFortySixtyMiniModal1V2, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'richText',
      selector: '[data-part="title"]',
      placement: 'right',
      options: {
        formatting: false,
      },
    },
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    person: {
      path: ['requires', 'person'],
      control: 'group',
      selector: '[data-part="person"]',
      placement: 'bottom',
      fields: [
        {
          path: ['avatar'],
          control: 'image',
          options: {
            helpLabel: 'Recommended size: 250x250px; < 100kB',
          },
        },
        {
          path: ['name'],
          control: 'text',
          label: 'Name',
        },
        {
          path: ['title'],
          control: 'text',
          label: 'Title',
        },
        {
          path: ['preposition'],
          control: 'text',
          label: 'Preposition',
        },
        {
          path: ['organization'],
          control: 'text',
          label: 'Company',
        },
      ],
    },
    primaryAction: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="primary-action"]',
      placement: 'top-end',
      label: 'Action',
    },
    otherActions: {
      path: ['requires', 'other_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="other-action"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
export { FortySixtyMiniModal1V2 };
